// The cut list: every part of every shop-made item on a job, as the engine worked them out
// when it priced the job. It is a view of the resolution, never typed and never stored on its
// own; a locked revision keeps it because it keeps the resolution. This is what a nesting
// program reads, and what the cut sheet is laid out from.

import { Rat } from './rational.ts';
import type { ResolvedItem } from './types.ts';

export interface CutListRow {
  item_id: string;
  item_number: string;
  item_name: string;
  part: string;
  /** The part's name on the job: the item's number, a dash, and its label (L-05-Top). */
  label: string;
  qty: string;
  length: string;
  width: string;
  grain: 'length' | 'width' | 'none';
  /** The grain may not be turned when the part is nested. */
  grain_locked: boolean;
  band_long: string;
  band_short: string;
  /** The banding roll on its banded edges, or null when it has none. */
  banding: string | null;
  material_id: string | null;
  material: string;
  note: string | null;
}

export interface CutListMaterial {
  material_id: string | null;
  material: string;
  /** How many pieces. */
  pieces: string;
  area_sq_in: string;
  /** Square feet, 2 places. */
  area_sq_ft: string;
  band_inches: string;
  rows: CutListRow[];
}

export interface CutList {
  materials: CutListMaterial[];
  pieces: string;
  band_inches: string;
}

const SQ_FT = Rat.of(144n);

/** The job's cut list, grouped by the material each part is cut from. */
export function cutList(items: Pick<ResolvedItem, 'id' | 'number' | 'name' | 'parts'>[]): CutList {
  const by = new Map<string, { m: CutListMaterial; pieces: Rat; area: Rat; band: Rat }>();
  let pieces = Rat.ZERO;
  let band = Rat.ZERO;
  for (const item of items) {
    for (const p of item.parts ?? []) {
      const key = p.material_id ?? `?${p.description}`;
      let g = by.get(key);
      if (!g) {
        g = { m: { material_id: p.material_id, material: p.description, pieces: '0', area_sq_in: '0', area_sq_ft: '0.00', band_inches: '0', rows: [] }, pieces: Rat.ZERO, area: Rat.ZERO, band: Rat.ZERO };
        by.set(key, g);
      }
      g.m.rows.push({
        item_id: item.id, item_number: item.number, item_name: item.name, part: p.name, label: p.label, qty: p.qty, length: p.length, width: p.width,
        grain: p.grain, grain_locked: p.grain_locked, band_long: p.band_long, band_short: p.band_short, banding: p.band_description, material_id: p.material_id, material: p.description, note: p.note,
      });
      const q = Rat.parse(p.qty);
      g.pieces = g.pieces.add(q);
      g.area = g.area.add(Rat.parse(p.area_sq_in));
      g.band = g.band.add(Rat.parse(p.band_inches));
      pieces = pieces.add(q);
      band = band.add(Rat.parse(p.band_inches));
    }
  }
  const materials = [...by.values()]
    .map((g) => ({ ...g.m, pieces: g.pieces.toDecimal(4), area_sq_in: g.area.toDecimal(4), area_sq_ft: g.area.div(SQ_FT).toFixed(2), band_inches: g.band.toDecimal(4) }))
    .sort((a, b) => a.material.localeCompare(b.material));
  return { materials, pieces: pieces.toDecimal(4), band_inches: band.toDecimal(4) };
}

const cell = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** The cut list as a plain CSV, one line per part line, for a nesting program to read. */
export function cutListCsv(list: CutList): string {
  const head = ['Material', 'Item', 'Label', 'Part', 'Qty', 'Length', 'Width', 'Grain', 'Grain locked', 'Banded length edges', 'Banded width edges', 'Banding', 'Note'];
  const lines = [head.join(',')];
  for (const m of list.materials) {
    for (const r of m.rows) lines.push([m.material, r.item_number, r.label, r.part, r.qty, r.length, r.width, r.grain, r.grain_locked ? 'yes' : '', r.band_long, r.band_short, r.banding ?? '', r.note ?? ''].map((v) => cell(String(v))).join(','));
  }
  return `${lines.join('\r\n')}\r\n`;
}
